// Pure table utility functions with no external dependencies.
// These are extracted from Table.tsx so they can be imported in test environments
// without pulling in the component's runtime dependencies (global.helper, virtualization, etc.).

export type CellValue = string | null

export function getCellId(rowIndex: number, dataColumnIndex: number): string {
  return `${rowIndex}-${dataColumnIndex}`
}

export function cellIdToPosition(cellId: string): {
  rowIndex: number
  columnIndex: number
} {
  const [rowIndex, columnIndex] = cellId.split('-').map(Number)
  return { rowIndex, columnIndex }
}

export function getSpecialKey(ev: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }): {
  isCtrlPressed?: boolean
  isShiftPressed?: boolean
  isSpecialKeyPressed?: boolean
} {
  const isCtrlPressed = ev.ctrlKey || ev.metaKey
  const isShiftPressed = ev.shiftKey
  const isSpecialKeyPressed = isCtrlPressed || isShiftPressed

  return {
    isCtrlPressed,
    isShiftPressed,
    isSpecialKeyPressed,
  }
}

export function getRectangularCellSelection(
  anchor: { rowIndex: number; tableColumnIndex: number },
  current: { rowIndex: number; tableColumnIndex: number },
  tableColumnToDataColumn: Record<number, number>
): Set<string> {
  const startRow = Math.min(anchor.rowIndex, current.rowIndex)
  const endRow = Math.max(anchor.rowIndex, current.rowIndex)
  const startCol = Math.min(anchor.tableColumnIndex, current.tableColumnIndex)
  const endCol = Math.max(anchor.tableColumnIndex, current.tableColumnIndex)

  const selection = new Set<string>()
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      selection.add(getCellId(r, tableColumnToDataColumn[c]))
    }
  }
  return selection
}

export type CellState = 'selected' | 'empty' | 'edited' | 'normal'

export const CELL_STATE_CLASSES: Record<CellState, string> = {
  selected: 'border-2 border-blue-900 bg-blue-400 text-blue-950 shadow-md shadow-blue-900',
  empty: 'border border-red-900 bg-red-200 text-stone-900',
  edited: 'border border-green-500 bg-green-200',
  normal: 'border border-gray-200 text-stone-900',
}

/** Highest-priority state wins; a cell only ever paints one of these. */
export function getCellState({
  isSelected,
  isEmpty,
  isEdited,
  readOnly,
}: {
  isSelected: boolean
  isEmpty: boolean
  isEdited: boolean
  readOnly: boolean
}): CellState {
  if (isSelected) return 'selected'
  if (isEmpty && !readOnly) return 'empty'
  if (isEdited) return 'edited'
  return 'normal'
}

/** `key` identifies the group, so neighbouring groups that happen to share a label stay apart. */
export type ColumnGroup = {
  key: string
  label: string
}

export type ColumnGroupSpan = ColumnGroup & {
  /** Index of the span's first column among the visible data columns, the time column not counted. */
  startIndex: number
  columnCount: number
}

/**
 * The runs of adjacent visible columns that share a group, so hidden columns shrink or remove a
 * group. `columnGroups` is indexed like the header row; a column without a group belongs to none.
 */
export function getColumnGroupSpans(
  columnGroups: readonly (ColumnGroup | null)[],
  tableColumnToDataColumn: Record<number, number>
): ColumnGroupSpan[] {
  const spans: ColumnGroupSpan[] = []
  const tableColumnCount = Object.keys(tableColumnToDataColumn).length

  for (let tableColumnIndex = 1; tableColumnIndex < tableColumnCount; tableColumnIndex++) {
    const group = columnGroups[tableColumnToDataColumn[tableColumnIndex]] ?? null
    const lastSpan = spans[spans.length - 1]

    if (group === null) {
      continue
    }
    if (lastSpan?.key === group.key && lastSpan.startIndex + lastSpan.columnCount === tableColumnIndex - 1) {
      lastSpan.columnCount++
    } else {
      spans.push({ key: group.key, label: group.label, startIndex: tableColumnIndex - 1, columnCount: 1 })
    }
  }

  return spans
}

/**
 * How far to push a group label right so it stays readable while its span is scrolled partly out of
 * view, without pushing it out of the span's far edge.
 */
export function getGroupLabelOffset({
  spanLeft,
  spanWidth,
  scrollLeft,
  minVisibleWidth,
}: {
  spanLeft: number
  spanWidth: number
  scrollLeft: number
  minVisibleWidth: number
}): number {
  return Math.max(0, Math.min(scrollLeft - spanLeft, spanWidth - minVisibleWidth))
}

/**
 * The divider where a unit group ends, as dark as the one after the time column. It straddles the
 * boundary at 2px so it covers both neighbours' 1px borders; the caller places it.
 */
export const GROUP_DIVIDER_CLASS = 'pointer-events-none absolute z-10 w-0.5 bg-gray-500'

/** Light grey behind every other visible column in a grouped table, so neighbouring columns read as separate. */
export const SHADED_COLUMN_CLASS = 'bg-gray-50'

export function isShadedColumn(visibleColumnIndex: number): boolean {
  return visibleColumnIndex % 2 === 1
}
